// src/features/landing/presentation/pages/SurveyPage.jsx
// 설문 페이지 — /survey/:code (설문 1개 = URL 1개)
// 설문 정의(cover·questions·version·status)는 recruit-api GET /api/surveys/{code}로 받아 그린다.
// 흐름: 표지 → 질문(설문 정의 순서) → 회사 정보(회사명·이름·연락처·개인정보 동의) → 설문 완료 안내.
// 그 밖의 화면: 불러오는 중 / 없는 설문(404) / 불러오기 실패(다시 시도) / 마감(status=CLOSED 또는 제출 시 마감 400).
// 제출 오류: 400은 서버 message 그대로, 409(설문 version 변경)는 새로고침 안내 + 새로고침 버튼.
// GA M&A 가치평가 화면(redesign 시안)을 설문용으로 옮긴 것 — 추정 가치·배수 표시는 모두 뺐다.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import {
    SURVEY_CLOSED_MESSAGE,
    buildAnswerPayload,
    canProceed,
    formatAnswer,
    isAnswered,
    validateSurveyContact,
} from "../../domain/surveys";
import { formatPhone, readUtm } from "../../domain/rbBridgeConsult";
import { surveyRepository } from "../../infrastructure/surveyApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import "../styles/landing.css";

const EMPTY_CONTACT = { company: "", name: "", phone: "", privacyAgreed: false };
const CONTACT_FIELDS = ["company", "name", "phone", "privacyAgreed"];
const VERSION_CONFLICT_MESSAGE = "설문 내용이 변경되었습니다. 새로고침 후 다시 응답해 주세요";

const prevIcon = (
    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const fmtNum = (v, dec) => v.toLocaleString("ko-KR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

function NumberField({ field: rawField, value, onChange }) {
    const field = { ...rawField, dec: rawField.dec ?? 0, ticks: rawField.ticks || [] };
    const [text, setText] = useState(value == null ? "" : value.toFixed(field.dec));

    // 슬라이더로 바뀐 값만 입력칸에 반영 — 입력 중인 "0." 같은 텍스트는 덮어쓰지 않는다
    useEffect(() => {
        setText((prev) => (value != null && prev !== "" && +prev === value ? prev : value == null ? "" : value.toFixed(field.dec)));
    }, [value, field.dec]);

    const clamp = (v) => {
        const c = Math.min(field.max, Math.max(field.min, v));
        // 서버와 같이 min 기준 step 배수로 맞춘다
        let stepped = field.min + Math.round((c - field.min) / field.step) * field.step;
        if (stepped > field.max) stepped -= field.step;
        return +stepped.toFixed(field.dec);
    };

    const commitText = () => {
        if (text.trim() === "" || isNaN(+text)) {
            setText(value == null ? "" : value.toFixed(field.dec));
            return;
        }
        onChange(clamp(+text));
    };

    const pct = value == null ? 0 : ((value - field.min) / (field.max - field.min)) * 100;

    return (
        <div className="svp-field">
            <div className="svp-f-head">
                <label className="svp-f-label" htmlFor={`svp-${field.key}`}>
                    {field.label}
                    {field.hint && <small>{field.hint}</small>}
                </label>
                <div className="svp-val">
                    <input
                        id={`svp-${field.key}`}
                        type="text"
                        inputMode={field.dec > 0 ? "decimal" : "numeric"}
                        placeholder="—"
                        value={text}
                        onChange={(e) => {
                            const t = e.target.value.replace(/[^\d.]/g, "");
                            setText(t);
                            if (t !== "" && !isNaN(+t) && +t >= field.min && +t <= field.max) onChange(+t);
                        }}
                        onBlur={commitText}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    />
                    <em>{field.unit}</em>
                </div>
            </div>
            <div className={`svp-rng${value == null ? " svp-rng--empty" : ""}`}>
                <input
                    type="range"
                    aria-label={field.label}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={value == null ? field.min : value}
                    style={{ "--p": `${pct}%` }}
                    onChange={(e) => onChange(clamp(+e.target.value))}
                />
                <div className="svp-ticks">
                    {field.ticks.map(([pos, label], i) => (
                        <span key={i} style={{ left: `${((pos - field.min) / (field.max - field.min)) * 100}%` }}>{label}</span>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function SurveyPage() {
    const { code } = useParams();
    const { accessToken } = useAuthContext();

    const [survey, setSurvey] = useState(null);
    const [loadState, setLoadState] = useState("loading"); // loading · ready · notFound · error
    const [loadError, setLoadError] = useState("");
    const [loadKey, setLoadKey] = useState(0); // 불러오기 실패 시 "다시 시도"

    const questions = survey && Array.isArray(survey.questions) ? survey.questions : [];
    const cover = (survey && survey.cover) || {};
    const closed = survey?.status === "CLOSED";
    const total = questions.length + 1; // 질문 + 회사 정보
    const contactStep = questions.length + 1;
    const doneStep = questions.length + 2;

    const [step, setStep] = useState(0); // 0 표지 · 1..N 질문 · N+1 회사 정보 · N+2 완료
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState(EMPTY_CONTACT);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [versionConflict, setVersionConflict] = useState(false);

    useEffect(() => {
        setStep(0);
        setAnswers({});
        setContact(EMPTY_CONTACT);
        setErrors({});
        setSubmitError("");
        setVersionConflict(false);
    }, [code]);

    useEffect(() => {
        let cancelled = false;
        setLoadState("loading");
        setLoadError("");
        surveyRepository
            .getSurvey(code)
            .then((data) => {
                if (cancelled) return;
                setSurvey(data);
                setLoadState("ready");
            })
            .catch((err) => {
                if (cancelled) return;
                setSurvey(null);
                if (err.status === 404) {
                    setLoadState("notFound");
                } else {
                    setLoadError(err.message);
                    setLoadState("error");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [code, loadKey]);

    const go = (n) => {
        setStep(n);
        window.scrollTo(0, 0);
    };

    const question = step >= 1 && step <= questions.length ? questions[step - 1] : null;
    const canNext = question ? canProceed(question, answers) : true;

    const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

    const handleContactChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === "checkbox" ? checked : name === "phone" ? formatPhone(value) : value;
        setContact((prev) => ({ ...prev, [name]: next }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleSubmit = async () => {
        const found = validateSurveyContact(contact);
        setErrors(found);
        if (Object.keys(found).length > 0) {
            const first = CONTACT_FIELDS.find((k) => found[k]);
            document.querySelector(`.svp-contact [name="${first}"]`)?.focus();
            return;
        }
        setSubmitting(true);
        setSubmitError("");
        setVersionConflict(false);
        try {
            await surveyRepository.submitResponse(
                survey.code,
                {
                    version: survey.version,
                    answers: buildAnswerPayload(questions, answers),
                    company: contact.company.trim(),
                    name: contact.name.trim(),
                    phone: contact.phone.replace(/\D/g, ""),
                    privacyAgreed: true,
                    ...readUtm(),
                },
                accessToken,
            );
            go(doneStep);
        } catch (err) {
            if (err.status === 409) {
                // 응답 중에 설문 정의(version)가 바뀜 — 새 정의로 다시 응답해야 한다
                setVersionConflict(true);
                setSubmitError(err.message || VERSION_CONFLICT_MESSAGE);
            } else if (err.status === 400 && err.message === SURVEY_CLOSED_MESSAGE) {
                // 응답 중에 마감됨 — 마감 안내 화면으로 전환
                setSurvey((prev) => ({ ...prev, status: "CLOSED" }));
                window.scrollTo(0, 0);
            } else {
                setSubmitError(err.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const next = () => {
        if (step === contactStep) handleSubmit();
        else if (canNext) go(step + 1);
    };

    const nextLabel = step === contactStep ? (submitting ? "제출 중…" : "설문 제출") : "다음";
    const inWizard = step >= 1 && step <= contactStep;
    const progress = step === 0 ? 0 : Math.min(step, total) / total * 100;

    const summaryRows = questions.map((q, i) => ({ key: q.id, label: q.summary || q.title, value: i + 1 <= step ? formatAnswer(q, answers) : null, cur: i + 1 === step }));

    return (
        <>
            <style>{`
                .svp {
                    /* /rb-bridge(RbBridgePage)·상담 신청(RbBridgeConsultPage)과 같은 팔레트 — 잉크·크림 바탕에 오렌지 강조, 브라운 라벨 */
                    --ink: #1c1814; --ink-2: #4a433d; --ink-3: #8a8079; --ink-4: #b8aea6;
                    --paper: #f7f4f1; --sheet: #FFFFFF; --line: #e5ddd7; --line-2: #d6cbc2; --tint: #f7f4f1;
                    --accent: #f16102; --accent-hover: #d95502; --accent-soft: #fdeee4; --eyebrow: #c2926b;
                    --gutter: clamp(16px, 4vw, 40px);
                    background: var(--paper); color: var(--ink); font-size: 15px; line-height: 1.6; text-align: left;
                    word-break: keep-all; overflow-wrap: anywhere; min-height: calc(100vh - 80px);
                    font-family: Pretendard, "Apple SD Gothic Neo", "Malgun Gothic", system-ui, sans-serif;
                }
                .svp *, .svp *::before, .svp *::after { box-sizing: border-box; }
                .svp button { font: inherit; }
                .svp input { font: inherit; color: inherit; }

                .svp-hd { position: relative; border-bottom: 1px solid var(--line); background: var(--paper); }
                .svp-hd-in { max-width: 1120px; margin: 0 auto; padding: 0 var(--gutter); height: 60px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
                .svp-mark { display: flex; align-items: baseline; gap: 10px; }
                .svp-mark b { font-weight: 800; color: var(--ink); font-size: 17px; letter-spacing: -0.02em; }
                .svp-mark span { font-size: 12px; color: var(--ink-3); letter-spacing: 0.08em; }
                .svp-hd-step { font-size: 13px; color: var(--ink-3); }
                .svp-hd-step b { color: var(--ink); font-weight: 600; }
                .svp-bar { position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; }
                .svp-bar i { display: block; height: 100%; background: var(--accent); transition: width .35s ease; }

                .svp-btn { appearance: none; border: 1px solid var(--accent); background: var(--accent); color: #fff; height: 52px; padding: 0 28px; border-radius: 8px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 10px; transition: background .15s; }
                .svp-btn:hover { background: var(--accent-hover); border-color: var(--accent-hover); }
                .svp-btn:focus-visible, .svp-opt:focus-visible, .svp-prev:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
                .svp-btn[disabled] { background: var(--line-2); border-color: var(--line-2); color: #fff; cursor: not-allowed; }
                .svp-prev { appearance: none; height: 52px; padding: 0 24px 0 18px; border: 1px solid var(--line); border-radius: 8px; background: var(--sheet); color: var(--ink); font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: border-color .15s, color .15s, background .15s; }
                .svp-prev svg { width: 16px; height: 16px; flex: none; transition: transform .15s; }
                .svp-prev:hover { border-color: var(--eyebrow); color: var(--eyebrow); }
                .svp-prev:hover svg { transform: translateX(-2px); }

                .svp-cover { max-width: 1120px; margin: 0 auto; padding: clamp(48px, 9vw, 104px) var(--gutter) 80px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 420px); gap: clamp(40px, 7vw, 96px); align-items: start; }
                .svp-kicker { font-size: 13px; color: var(--eyebrow); letter-spacing: 0.08em; font-weight: 700; text-transform: uppercase; margin: 0; }
                .svp-cover h1 { white-space: pre-line; font-weight: 800; font-size: clamp(34px, 5.2vw, 54px); line-height: 1.25; letter-spacing: -0.035em; margin: 18px 0 0; }
                .svp-lead { margin: 24px 0 0; font-size: 17px; color: var(--ink-2); max-width: 30em; }
                .svp-cover-act { margin-top: 40px; display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
                .svp-cover-meta { font-size: 13px; color: var(--ink-3); }
                .svp-cover-meta span + span::before { content: ""; display: inline-block; width: 3px; height: 3px; border-radius: 50%; background: var(--ink-4); margin: 0 10px; vertical-align: middle; }
                .svp-outline { width: 100%; border-collapse: collapse; border-top: 1px solid var(--ink); margin-top: 6px; }
                .svp-outline caption { text-align: left; font-size: 13px; color: var(--ink-3); padding-bottom: 12px; }
                .svp-outline td { padding: 13px 0; border-bottom: 1px solid var(--line); font-size: 14.5px; vertical-align: baseline; }
                .svp-outline td:first-child { width: 44px; color: var(--ink-4); font-size: 13px; font-variant-numeric: tabular-nums; }
                .svp-outline td:last-child { text-align: right; color: var(--ink-3); font-size: 13px; }

                .svp-wz { max-width: 1120px; margin: 0 auto; padding: clamp(32px, 6vw, 64px) var(--gutter) 140px; display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: clamp(32px, 6vw, 80px); align-items: start; }
                .svp-q-no { font-size: 13px; color: var(--eyebrow); letter-spacing: 0.08em; font-weight: 700; margin: 0; }
                .svp-q-title { font-weight: 800; font-size: clamp(26px, 3.4vw, 34px); line-height: 1.35; letter-spacing: -0.03em; margin: 12px 0 0; }
                .svp-q-desc { margin: 12px 0 0; color: var(--ink-2); font-size: 15.5px; max-width: 34em; }
                .svp-q-body { margin-top: 36px; animation: svp-rise .28s ease both; }
                @keyframes svp-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
                @media (prefers-reduced-motion: reduce) { .svp-q-body { animation: none; } .svp-bar i { transition: none; } }

                .svp-opts { border-top: 1px solid var(--line-2); }
                .svp-opt { appearance: none; width: 100%; background: none; border: 0; border-bottom: 1px solid var(--line); display: grid; grid-template-columns: 22px minmax(0, 1fr); gap: 16px; align-items: center; text-align: left; padding: 20px 4px; cursor: pointer; position: relative; transition: background .15s; }
                .svp-opt:hover { background: rgba(28, 24, 20, .025); }
                .svp-opt .rd { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid var(--ink-4); position: relative; }
                .svp-opt .rd::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: var(--accent); transform: scale(0); transition: transform .15s; }
                .svp-opt .t { display: block; font-size: 16.5px; font-weight: 600; color: var(--ink); }
                .svp-opt .s { display: block; font-size: 13.5px; color: var(--ink-3); margin-top: 2px; }
                .svp-opt[aria-checked="true"] .rd { border-color: var(--accent); }
                .svp-opt[aria-checked="true"] .rd::after { transform: scale(1); }
                .svp-opt[aria-checked="true"]::before { content: ""; position: absolute; left: -15px; top: -1px; bottom: -1px; width: 2px; background: var(--accent); }

                .svp-field + .svp-field { margin-top: 44px; }
                .svp-f-head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; }
                .svp-f-label { font-size: 14px; color: var(--ink-2); font-weight: 600; }
                .svp-f-label small { display: block; font-weight: 400; color: var(--ink-3); font-size: 13px; margin-top: 2px; }
                .svp-val { display: flex; align-items: baseline; gap: 6px; border-bottom: 1px solid var(--line-2); padding-bottom: 4px; transition: border-color .15s; }
                .svp-val:focus-within { border-color: var(--ink); }
                .svp-val input { border: 0; background: transparent; font-weight: 600; font-size: clamp(34px, 4.6vw, 46px); line-height: 1.1; letter-spacing: -0.02em; width: 4.2ch; text-align: right; padding: 0; outline: none; font-variant-numeric: tabular-nums; }
                .svp-val input::placeholder { color: var(--ink-4); }
                .svp-val em { font-style: normal; font-size: 15px; color: var(--ink-2); white-space: nowrap; }
                .svp-rng { position: relative; margin-top: 22px; padding-bottom: 26px; }
                .svp-rng input[type=range] { -webkit-appearance: none; appearance: none; width: 100%; height: 28px; background: transparent; margin: 0; cursor: pointer; display: block; }
                .svp-rng input[type=range]:focus { outline: none; }
                .svp-rng input[type=range]::-webkit-slider-runnable-track { height: 2px; background: linear-gradient(var(--accent), var(--accent)) 0 / var(--p) 100% no-repeat, var(--line-2); }
                .svp-rng input[type=range]::-moz-range-track { height: 2px; background: var(--line-2); }
                .svp-rng input[type=range]::-moz-range-progress { height: 2px; background: var(--accent); }
                .svp-rng input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2px solid var(--accent); margin-top: -9px; box-shadow: 0 1px 3px rgba(28, 24, 20, .18); }
                .svp-rng input[type=range]::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: #fff; border: 2px solid var(--accent); }
                .svp-rng input[type=range]:focus-visible::-webkit-slider-thumb { outline: 2px solid var(--eyebrow); outline-offset: 2px; }
                .svp-rng--empty input[type=range]::-webkit-slider-thumb { border-color: var(--ink-4); box-shadow: none; }
                .svp-rng--empty input[type=range]::-moz-range-thumb { border-color: var(--ink-4); }
                .svp-ticks { position: absolute; left: 10px; right: 10px; bottom: 0; height: 20px; font-size: 12px; color: var(--ink-3); }
                .svp-ticks span { position: absolute; transform: translateX(-50%); white-space: nowrap; }
                .svp-ticks span:first-child { transform: none; left: -10px !important; }
                .svp-ticks span:last-child { transform: none; left: auto !important; right: -10px; }
                .svp-note { margin-top: 22px; font-size: 13.5px; color: var(--ink-3); line-height: 1.65; }
                .svp-sumline { margin-top: 40px; border-top: 1px solid var(--ink); display: flex; justify-content: space-between; align-items: baseline; padding-top: 16px; gap: 12px; }
                .svp-sumline span { font-size: 14px; color: var(--ink-2); }
                .svp-sumline b { font-size: 24px; font-weight: 600; font-variant-numeric: tabular-nums; }

                .svp-contact { display: grid; gap: 24px; }
                .svp-input { display: grid; gap: 8px; }
                .svp-input label { font-size: 14px; color: var(--ink-2); font-weight: 600; }
                .svp-input label em { font-style: normal; color: var(--eyebrow); margin-left: 2px; }
                .svp-input input { width: 100%; height: 52px; padding: 0 16px; border: 1px solid var(--line); border-radius: 8px; background: var(--sheet); font-size: 16px; }
                .svp-input input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px rgba(241, 97, 2, 0.12); }
                .svp-input input[aria-invalid="true"] { border-color: #B42318; }
                .svp-error { font-size: 13px; color: #B42318; }
                .svp-privacy { padding: 18px 20px; background: var(--tint); border: 1px solid var(--line); border-radius: 10px; }
                .svp-privacy-title { margin: 0 0 10px; font-size: 13px; font-weight: 700; }
                .svp-privacy dl { margin: 0; }
                .svp-privacy dl > div { display: grid; grid-template-columns: 72px minmax(0, 1fr); gap: 8px; padding: 3px 0; font-size: 13px; line-height: 1.6; }
                .svp-privacy dt { color: var(--ink-3); }
                .svp-privacy dd { margin: 0; color: var(--ink-2); }
                .svp-privacy-note { margin: 8px 0 14px; font-size: 12px; color: var(--ink-3); }
                .svp-check { display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer; }
                .svp-check input { width: 18px; height: 18px; accent-color: var(--accent); margin: 0; }
                .svp-submit-error { padding: 12px 14px; border-left: 2px solid #B42318; background: #FEF3F2; font-size: 14px; color: #B42318; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: 0; }
                .svp-submit-error button { appearance: none; border: 1px solid #B42318; background: #fff; color: #B42318; height: 36px; padding: 0 14px; border-radius: 6px; font-size: 13.5px; font-weight: 700; cursor: pointer; flex: none; }
                .svp-submit-error button:hover { background: #FEE4E2; }

                .svp-nav { margin-top: 48px; padding-top: 28px; border-top: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; gap: 16px; }
                .svp-nav .svp-btn { min-width: 148px; }

                .svp-panel { position: sticky; top: 100px; background: var(--sheet); border: 1px solid var(--line); border-radius: 16px; padding: 26px 26px 22px; }
                .svp-p-label { font-size: 12.5px; color: var(--ink-3); letter-spacing: 0.06em; margin: 0; }
                .svp-p-count { font-size: 28px; font-weight: 600; letter-spacing: -0.02em; margin: 6px 0 0; font-variant-numeric: tabular-nums; }
                .svp-p-count small { font-size: 15px; font-weight: 500; color: var(--ink-3); margin-left: 4px; }
                .svp-p-tbl { width: 100%; border-collapse: collapse; margin-top: 18px; border-top: 1px solid var(--line); }
                .svp-p-tbl th, .svp-p-tbl td { padding: 9px 0; border-bottom: 1px solid var(--line); font-size: 13px; text-align: left; font-weight: 400; vertical-align: baseline; }
                .svp-p-tbl th { color: var(--ink-3); width: 44%; }
                .svp-p-tbl td { text-align: right; color: var(--ink); }
                .svp-p-tbl td.empty { color: var(--ink-4); }
                .svp-p-tbl tr.cur th { color: var(--ink); font-weight: 600; }
                .svp-p-foot { font-size: 12px; color: var(--ink-4); margin: 14px 0 0; line-height: 1.55; }

                .svp-mbar { display: none; }

                .svp-done { max-width: 720px; margin: 0 auto; padding: clamp(56px, 10vw, 112px) var(--gutter) 96px; }
                .svp-done-sheet { background: var(--sheet); border: 1px solid var(--line); border-radius: 16px; padding: clamp(32px, 6vw, 48px); }
                .svp-done h1 { font-weight: 800; font-size: clamp(28px, 4vw, 38px); letter-spacing: -0.03em; line-height: 1.3; margin: 14px 0 0; }
                .svp-done-lead { margin: 16px 0 0; font-size: 16px; color: var(--ink-2); }
                .svp-steps { list-style: none; margin: 32px 0 0; padding: 0; border-top: 1px solid var(--ink); }
                .svp-steps li { display: grid; grid-template-columns: 44px minmax(0, 1fr); padding: 16px 0; border-bottom: 1px solid var(--line); }
                .svp-steps li span { color: var(--ink-4); font-size: 13px; font-variant-numeric: tabular-nums; padding-top: 2px; }
                .svp-steps b { display: block; font-size: 15px; font-weight: 600; }
                .svp-steps p { margin: 4px 0 0; font-size: 14px; color: var(--ink-3); }
                .svp-done-foot { margin: 24px 0 0; font-size: 13px; color: var(--ink-3); }

                .svp-missing { max-width: 640px; margin: 0 auto; padding: 120px var(--gutter); text-align: center; }
                .svp-missing h1 { font-size: 26px; font-weight: 700; margin: 0; }
                .svp-missing p { margin: 12px 0 0; color: var(--ink-3); }
                .svp-missing .svp-btn { margin-top: 28px; }

                @media (max-width: 960px) {
                    .svp-cover { grid-template-columns: 1fr; gap: 48px; }
                    .svp-wz { grid-template-columns: 1fr; padding-bottom: 180px; }
                    .svp-panel { display: none; }
                    .svp-mbar { display: flex; position: fixed; left: 0; right: 0; bottom: 0; z-index: 30; background: var(--sheet); border-top: 1px solid var(--line); padding: 12px var(--gutter) calc(12px + env(safe-area-inset-bottom)); align-items: center; gap: 8px; }
                    .svp-mbar .svp-prev { height: 48px; padding: 0 16px 0 12px; flex: none; }
                    .svp-mbar .svp-btn { height: 48px; padding: 0 22px; flex: 1; }
                    .svp-nav { display: none; }
                    .svp-opt[aria-checked="true"]::before { display: none; }
                }
                @media (max-width: 600px) {
                    .svp { font-size: 14.5px; }
                    .svp-hd-in { height: 54px; }
                    .svp-mark span { display: none; }
                    .svp-lead { font-size: 16px; }
                    .svp-cover-act .svp-btn { width: 100%; }
                    .svp-opt { padding: 18px 2px; gap: 14px; }
                    .svp-opt .t { font-size: 16px; }
                }
            `}</style>

            <main className="svp">
                {loadState === "loading" ? (
                    <div className="svp-missing" role="status">
                        <p>설문을 불러오는 중입니다…</p>
                    </div>
                ) : loadState === "notFound" ? (
                    <div className="svp-missing">
                        <h1>설문을 찾을 수 없습니다</h1>
                        <p>주소가 정확한지 확인해 주세요. 받으신 링크로 다시 접속해 주시기 바랍니다.</p>
                    </div>
                ) : loadState === "error" || !survey ? (
                    <div className="svp-missing" role="alert">
                        <h1>설문을 불러오지 못했습니다</h1>
                        <p>{loadError || "잠시 후 다시 시도해 주세요."}</p>
                        <button type="button" className="svp-btn" onClick={() => setLoadKey((k) => k + 1)}>다시 시도</button>
                    </div>
                ) : (
                    <>
                        <header className="svp-hd">
                            <div className="svp-hd-in">
                                <div className="svp-mark"><b>RB Bridge</b><span>{cover.kicker}</span></div>
                                {!closed && inWizard && (
                                    <div className="svp-hd-step">
                                        {step <= questions.length ? "질문" : "마지막 단계"} <b>{step}</b> / {total}
                                    </div>
                                )}
                            </div>
                            <div className="svp-bar"><i style={{ width: `${progress}%` }} /></div>
                        </header>

                        {closed && (
                            <div className="svp-missing">
                                <h1>마감된 설문입니다</h1>
                                <p>응답 기간이 끝나 더 이상 응답을 받지 않습니다. 관심 가져 주셔서 감사합니다.</p>
                            </div>
                        )}

                        {!closed && step === 0 && (
                            <section className="svp-cover">
                                <div>
                                    {cover.kicker && <p className="svp-kicker">{cover.kicker}</p>}
                                    <h1>{cover.title}</h1>
                                    {cover.lead && <p className="svp-lead">{cover.lead}</p>}
                                    <div className="svp-cover-act">
                                        <button type="button" className="svp-btn" onClick={() => go(1)}>설문 시작하기</button>
                                        <div className="svp-cover-meta">{cover.duration && <span>{cover.duration}</span>}<span>담당자만 확인</span></div>
                                    </div>
                                </div>
                                <table className="svp-outline">
                                    <caption>질문 항목</caption>
                                    <tbody>
                                        {questions.map((q, i) => (
                                            <tr key={q.id}>
                                                <td>{String(i + 1).padStart(2, "0")}</td>
                                                <td>{q.summary || q.title}</td>
                                                <td>{q.category}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td>{String(total).padStart(2, "0")}</td>
                                            <td>회사명 · 이름 · 연락처</td>
                                            <td>회사 정보</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>
                        )}

                        {!closed && inWizard && (
                            <div className="svp-wz">
                                <div>
                                    {question ? (
                                        <>
                                            <p className="svp-q-no">QUESTION {String(step).padStart(2, "0")}</p>
                                            <h2 className="svp-q-title">{question.title}</h2>
                                            {question.desc && <p className="svp-q-desc">{question.desc}</p>}
                                            <div className="svp-q-body" key={question.id}>
                                                {question.type === "choice" && (
                                                    <div className="svp-opts" role="radiogroup" aria-label={question.title}>
                                                        {question.options.map((o) => (
                                                            <button
                                                                key={o.value}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={answers[question.id] === o.value}
                                                                className="svp-opt"
                                                                onClick={() => setAnswer(question.id, o.value)}
                                                            >
                                                                <span className="rd" aria-hidden="true" />
                                                                <span>
                                                                    <span className="t">{o.label}</span>
                                                                    {o.sub && <span className="s">{o.sub}</span>}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                {question.type === "number" && question.fields.map((f) => (
                                                    <NumberField key={f.key} field={f} value={answers[f.key] ?? null} onChange={(v) => setAnswer(f.key, v)} />
                                                ))}
                                                {question.annualSum && (() => {
                                                    const vals = question.annualSum.map((k) => answers[k]);
                                                    const ready = vals.every((v) => v != null);
                                                    const monthly = ready ? vals.reduce((a, b) => a + b, 0) : null;
                                                    return (
                                                        <div className="svp-sumline">
                                                            <span>연환산 매출{ready ? ` (월 ${fmtNum(monthly, 1)}억 × 12개월)` : ""}</span>
                                                            <b>{ready ? `${fmtNum(monthly * 12, 1)}억` : "—"}</b>
                                                        </div>
                                                    );
                                                })()}
                                                {question.note && <p className="svp-note">{question.note}</p>}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="svp-q-no">LAST STEP</p>
                                            <h2 className="svp-q-title">회사 정보를 남겨 주세요</h2>
                                            <p className="svp-q-desc">응답 내용을 확인한 뒤 필요한 경우 담당자가 연락드립니다.</p>
                                            <div className="svp-q-body svp-contact" key="contact">
                                                <div className="svp-input">
                                                    <label htmlFor="svp-company">회사명<em>*</em></label>
                                                    <input id="svp-company" name="company" value={contact.company} onChange={handleContactChange}
                                                        maxLength={100} placeholder="예) 라이징브로커즈" aria-invalid={!!errors.company} />
                                                    {errors.company && <span className="svp-error">{errors.company}</span>}
                                                </div>
                                                <div className="svp-input">
                                                    <label htmlFor="svp-name">이름<em>*</em></label>
                                                    <input id="svp-name" name="name" value={contact.name} onChange={handleContactChange}
                                                        maxLength={50} autoComplete="name" placeholder="예) 홍길동" aria-invalid={!!errors.name} />
                                                    {errors.name && <span className="svp-error">{errors.name}</span>}
                                                </div>
                                                <div className="svp-input">
                                                    <label htmlFor="svp-phone">연락처<em>*</em></label>
                                                    <input id="svp-phone" name="phone" value={contact.phone} onChange={handleContactChange}
                                                        inputMode="tel" placeholder="010-0000-0000" aria-invalid={!!errors.phone} />
                                                    {errors.phone && <span className="svp-error">{errors.phone}</span>}
                                                </div>
                                                <div className="svp-privacy">
                                                    <p className="svp-privacy-title">개인정보 수집·이용 안내</p>
                                                    <dl>
                                                        <div><dt>수집 항목</dt><dd>회사명, 이름, 연락처, 설문 응답 내용</dd></div>
                                                        <div><dt>이용 목적</dt><dd>설문 응답 확인 및 RB Bridge 상담 연락</dd></div>
                                                        <div><dt>보유 기간</dt><dd>삭제를 요청하실 때까지 보관합니다</dd></div>
                                                    </dl>
                                                    <p className="svp-privacy-note">동의하지 않으실 수 있으며, 이 경우 설문 제출이 제한됩니다.</p>
                                                    <label className="svp-check">
                                                        <input type="checkbox" name="privacyAgreed" checked={contact.privacyAgreed} onChange={handleContactChange} />
                                                        <span>개인정보 수집·이용에 동의합니다. (필수)</span>
                                                    </label>
                                                    {errors.privacyAgreed && <span className="svp-error">{errors.privacyAgreed}</span>}
                                                </div>
                                                {submitError && (
                                                    <div className="svp-submit-error" role="alert">
                                                        <span>{submitError}</span>
                                                        {versionConflict && (
                                                            <button type="button" onClick={() => window.location.reload()}>새로고침</button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="svp-nav">
                                        <button type="button" className="svp-prev" onClick={() => go(step - 1)}>{prevIcon}이전</button>
                                        <button type="button" className="svp-btn" disabled={!canNext || submitting} onClick={next}>{nextLabel}</button>
                                    </div>
                                </div>

                                <aside className="svp-panel" aria-label="응답 요약">
                                    <p className="svp-p-label">응답 현황</p>
                                    <p className="svp-p-count">
                                        {questions.filter((q) => isAnswered(q, answers)).length}<small>/ {questions.length} 문항</small>
                                    </p>
                                    <table className="svp-p-tbl">
                                        <tbody>
                                            {summaryRows.map((r) => (
                                                <tr key={r.key} className={r.cur ? "cur" : undefined}>
                                                    <th>{r.label}</th>
                                                    <td className={r.value == null ? "empty" : undefined}>{r.value ?? "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="svp-p-foot">이전 버튼으로 돌아가 응답을 수정할 수 있습니다.</p>
                                </aside>

                                <div className="svp-mbar">
                                    <button type="button" className="svp-prev" onClick={() => go(step - 1)}>{prevIcon}이전</button>
                                    <button type="button" className="svp-btn" disabled={!canNext || submitting} onClick={next}>{nextLabel}</button>
                                </div>
                            </div>
                        )}

                        {!closed && step === doneStep && (
                            <section className="svp-done">
                                <div className="svp-done-sheet">
                                    <p className="svp-kicker">SURVEY COMPLETE</p>
                                    <h1>설문이 완료되었습니다</h1>
                                    <p className="svp-done-lead">귀한 시간 내어 응답해 주셔서 감사합니다.</p>
                                    <ol className="svp-steps">
                                        <li><span>01</span><div><b>응답 확인</b><p>남겨 주신 응답은 RB Bridge 담당자만 확인합니다.</p></div></li>
                                        <li><span>02</span><div><b>담당자 연락</b><p>추가로 여쭐 내용이 있으면 남겨 주신 연락처로 연락드립니다.</p></div></li>
                                    </ol>
                                    <p className="svp-done-foot">응답 내용은 동의 없이 외부에 공개되지 않습니다.</p>
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>
            <Footer />
        </>
    );
}
